import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowLeft, Send, MapPin, Mail, MessageCircle, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/support/tickets`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="contact-page">
      <header className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#1B3A6B]">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0F1A2A] mb-4" data-testid="contact-title">Contact Us</h1>
          <p className="text-lg text-[#475569]">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#1B3A6B]/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#1B3A6B]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-[#0F1A2A]">Email</h3>
                  <p className="text-sm text-[#475569]">info@thefeedingfaith.org</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#1B3A6B]/10 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#1B3A6B]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-[#0F1A2A]">Support Hours</h3>
                  <p className="text-sm text-[#475569]">Mon-Fri, 9am-5pm EST</p>
                </div>
              </div>
            </div>
            <div className="bg-[#1B3A6B] rounded-2xl p-6 text-white">
              <h3 className="font-heading font-bold mb-2">Need Quick Help?</h3>
              <p className="text-white/70 text-sm mb-4">Visit our support page for FAQs and common questions.</p>
              <Link to="/support" className="text-[#B8860B] text-sm font-medium hover:underline">Go to Support →</Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl p-12 shadow-sm text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-2xl font-bold text-[#0F1A2A] mb-2">Message Sent!</h2>
                <p className="text-[#475569] mb-6">Thank you for reaching out. We'll get back to you within 1-2 business days.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="text-[#1B3A6B] font-medium hover:underline">Send Another Message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm space-y-4" data-testid="contact-form">
                {error && <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Name</label>
                    <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" placeholder="Your name" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Email</label>
                    <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" placeholder="your@email.com" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Subject</label>
                  <input type="text" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" placeholder="How can we help?" data-testid="contact-subject" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Message</label>
                  <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={5} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] resize-none" placeholder="Tell us more..." data-testid="contact-message" />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3.5 bg-[#1B3A6B] text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#254B8A] transition-colors disabled:opacity-50" data-testid="contact-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-[#0F1A2A] text-white/60 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-sm">
          &copy; {new Date().getFullYear()} dsashun. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
